// @ts-nocheck
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { ShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getOrderState } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { ChangeRequestLikelihoodModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import constants from "@store/constants";
import clearChangeRequestOrder from "@store/Framework/Store/Greif/Pages/RequestOrderChange/Handler/ClearChangeRequestOrder";
import setChangeRequestOrder from "@store/Framework/Store/Greif/Pages/RequestOrderChange/Handler/SetChangeRequestOrder";
import updateChangeRequestOrder from "@store/Framework/Store/Greif/Pages/RequestOrderChange/Handler/UpdateChangeRequestOrder";
import LoadingOverlay from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import isEmpty from "lodash/isEmpty";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    orderState: getOrderState(state, state.pages.orderDetails.orderNumber),
    changeRequestOrderState: state.greif.pages.orderChange,
});

const mapDispatchToProps = {
    setChangeRequestOrder,
    clearChangeRequestOrder,
};

export interface RequestOrderChangePageContainerStyles {}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ChangeRequestLineModel {
    orderLineId: string;
    changeReason: string;
    changeNotes: string;
    newQuantity: number | string;
    originalQuantity: number | string;
    leadTimeInWeeks: string;
    isCanceled: boolean;
    plant: string;
}

export interface ChangeRequestModel {
    orderId: string;
    erpNumber: string;
    newPoNumber: string | undefined;
    newRequestedDate: Date | undefined;
    newLines: AddedItemModel[] | undefined;
    orderLines: ChangeRequestLineModel[];
    newShipToCompanyName: string | undefined;
    newShipToAddress1: string | undefined;
    newShipToCity: string | undefined;
    newShipToState: string | undefined;
    newShipToPostalCode: string | undefined;
    newShipToCountry: string | undefined;
    newShipToCode: string | undefined;
    likelihoodOfChange?: ChangeRequestLikelihoodModel;
}

export interface AddedItemModel {
    product: ProductModel;
    productInfo: ProductInfo;
    changeNotes: string | undefined;
    newQuantity: number | string | undefined;
    changeReason: string | undefined;
}

const orderChangeRequestInitialValues: ChangeRequestModel = {
    orderId: "",
    erpNumber: "",
    newRequestedDate: undefined,
    newPoNumber: undefined,
    newLines: [],
    orderLines: [],
    newShipToCompanyName: undefined,
    newShipToAddress1: undefined,
    newShipToCity: undefined,
    newShipToState: undefined,
    newShipToPostalCode: undefined,
    newShipToCountry: undefined,
    newShipToCode: undefined,
    likelihoodOfChange: undefined,
};

const RequestOrderChangePageContainer = ({
    id,
    orderState,
    changeRequestOrderState,
    setChangeRequestOrder,
    clearChangeRequestOrder,
}: Props) => {
    const order = orderState.value;
    const { changeRequestOrder, requestSubmitted } = changeRequestOrderState;
    const { isEditing } = useContext(ShellContext);

    if (isEditing && !order) {
        return <Zone contentId={id} zoneName="Container" />;
    }

    if (!order) {
        return null;
    }

    const [simplifiedOrder, setSimplifiedOrder] = useState<ChangeRequestModel>();

    const currentOrderRequestId = `orderChange-${order?.id}`;

    useEffect(() => {
        if (
            (!isEmpty(changeRequestOrder) && changeRequestOrder.orderId !== currentOrderRequestId) ||
            requestSubmitted
        ) {
            clearChangeRequestOrder({});
        }
    }, [changeRequestOrder, currentOrderRequestId]);

    useEffect(() => {
        const changeRequestOrderJson = sessionStorage.getItem(currentOrderRequestId);

        if (changeRequestOrderJson && changeRequestOrderJson.length > 0) {
            setTimeout(() => {
                const changeRequestOrderObj: ChangeRequestModel = JSON.parse(changeRequestOrderJson);
                setChangeRequestOrder({ changeRequestOrder: changeRequestOrderObj });
            }, 1000);

            return;
        }

        const simplifiedLines: ChangeRequestLineModel[] = [];
        order?.orderLines?.forEach(line => {
            const qtyRemaining = line.qtyOrdered - line.qtyShipped;
            const isLineCanceled = line.status.toLowerCase() === "canceled";

            simplifiedLines.push({
                orderLineId: line.id,
                changeNotes: "",
                changeReason: "",
                newQuantity: qtyRemaining,
                originalQuantity: qtyRemaining,
                isCanceled: isLineCanceled || false,
                leadTimeInWeeks: line?.properties["leadTime"],
                plant: line.warehouse,
            } as ChangeRequestLineModel);
        });

        const orderChangeRequest: ChangeRequestModel = {
            ...orderChangeRequestInitialValues,
            orderLines: simplifiedLines,
            orderId: currentOrderRequestId,
            erpNumber: order!.erpOrderNumber,
            newRequestedDate: undefined,
            newPoNumber: undefined,
            newLines: [],
            newShipToCompanyName: undefined,
            newShipToAddress1: undefined,
            newShipToCity: undefined,
            newShipToState: undefined,
            newShipToPostalCode: undefined,
            newShipToCountry: undefined,
            newShipToCode: undefined,
        };

        setSimplifiedOrder(orderChangeRequest);
        setChangeRequestOrder({ changeRequestOrder: orderChangeRequest });

        const strignifiedOrderRequest = JSON.stringify(orderChangeRequest);
        sessionStorage.setItem(currentOrderRequestId, strignifiedOrderRequest);
    }, [order, currentOrderRequestId]);

    useEffect(() => {
        if (changeRequestOrder) {
            const strignifiedOrderRequest = JSON.stringify(changeRequestOrder);
            sessionStorage.setItem(currentOrderRequestId, strignifiedOrderRequest);
        }
    }, [changeRequestOrder]);

    useEffect(() => {
        if (simplifiedOrder) {
            setChangeRequestOrder({ changeRequestOrder: simplifiedOrder });
        }
    }, [simplifiedOrder]);

    useEffect(() => {
        // Clear store value on unmount
        return () => {
            updateChangeRequestOrder({ changeRequestOrder: orderChangeRequestInitialValues });
        };
    }, []);

    return (
        <LoadingOverlay loading={!changeRequestOrder}>
            <Zone contentId={id} zoneName="Container" />
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RequestOrderChangePageContainer),
    definition: {
        group: constants.widgetGroup.orderChangeRequest,
        displayName: "Change Request Page Container",
        allowedContexts: ["OrderChangeRequestPage" as any],
    },
};

export default widgetModule;
